import { Component, effect, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import {
  ISofHeaderEvent,
  ISofMenuHeaderItem,
  ISofMenuItem,
  ISofNotificationsConfig,
  SofAppShellComponent,
  SofFloatingModalService,
  SofHeaderService,
  SofMenuService,
  SofNotificationsFloatingModalComponent,
  SofNotificationsFloatingModalService,
  SofToastService,
  SofUserFloatingModalComponent,
  SofUserMenuService,
} from '@softion/web-components';
import { Subject, filter, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../shared/services/auth.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [RouterModule, SofAppShellComponent],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly sofMenuService = inject(SofMenuService);
  private readonly sofHeaderService = inject(SofHeaderService);
  private readonly sofToastService = inject(SofToastService);
  private readonly sofFloatingModalService = inject(SofFloatingModalService);
  private readonly sofNotificationsFloatingModal = inject(SofNotificationsFloatingModalService);
  private readonly sofUserMenuService = inject(SofUserMenuService);
  private readonly auth = inject(AuthService);
  private readonly destroy$ = new Subject<void>();

  readonly logoFull = environment.logoFull;
  readonly logoSmall = environment.logoSmall;

  readonly idFloatingUser = 'floating-modal-user';

  private _isDark = false;

  private menu: ISofMenuItem[] = this._buildMenu();

  readonly menuHeader: ISofMenuHeaderItem[] = [
    {
      id: 'notifications',
      icon: 'fa fa-bell sof-color-info',
      tooltip: 'Notificaciones',
      action: 'floatingModal',
      badge: 0,
      disabled: false,
      show: true,
    },
    {
      id: 'theme',
      icon: 'fa fa-moon',
      tooltip: 'Tema',
      action: 'function',
      show: true,
    },
  ];

  readonly configNotifications: ISofNotificationsConfig = {
    showMarkAll: true,
    showClearAll: true,
    showPerItemMarkRead: true,
    showPerItemDelete: true,
    compactTime: true,
    maxHeight: 420,
  };

  constructor() {
    effect(() => {
      const user   = this.auth.user();
      const hasOrg = !!user?.orgId;

      // Rebuild the full menu so nested workspace items (org-sw-ecm, org-sw-qr,
      // org-sw-cnt) always reflect the current org name — patchItem only reaches
      // root-level items and misses children. Also update this.menu so that
      // syncMenuSelection passes fresh group objects to drillDown().
      this.menu = this._buildMenu(user);
      this.sofMenuService.setMenu(this.menu);
      this.syncMenuSelection(this.router.url);

      if (hasOrg && user?.storageProvisioned === false) {
        this.sofToastService.openToast({
          id: 'storage-not-provisioned',
          title: 'Storage no aprovisionado',
          message: 'El almacenamiento de tu organización aún no está configurado. Consulta al administrador.',
          position: 'top-right',
        });
      }
    });
  }

  ngOnInit(): void {
    this.initMenu();
    this.initHeader();
    this.initNotifications();
    this.initProfile();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initMenu(): void {
    this.sofMenuService.setDrilldown(true);
    this.sofMenuService.setMenu(this.menu);
    this.syncMenuSelection(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => this.syncMenuSelection(event.urlAfterRedirects));

    this.sofMenuService.itemClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe((item) => this.handleSelectMenu(item));
  }

  private initHeader(): void {
    const user = this.auth.user();
    this.sofHeaderService.setConfig({
      avatar: {
        show: true,
        fullName: user?.sName ?? 'Admin',
      },
    });

    const saved = localStorage.getItem('sof-color-scheme');
    if (saved === 'dark' || saved === 'light') {
      this._isDark = saved === 'dark';
      document.documentElement.dataset['colorScheme'] = saved;
    } else {
      this._isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    const themeBtn = this.menuHeader.find((m) => m.id === 'theme');
    if (themeBtn) {
      themeBtn.icon    = this._isDark ? 'fa fa-sun'  : 'fa fa-moon';
      themeBtn.tooltip = this._isDark ? 'Modo claro' : 'Modo oscuro';
    }

    this.sofHeaderService.setMenu(this.menuHeader);
    this.sofHeaderService.events$.subscribe((item) => this.handleSelectMenuHeader(item));
    this.sofHeaderService.avatar$.subscribe((item) => {
      if (item?.originalEvent) this.openFloatingUser(item.originalEvent);
    });
  }

  private initNotifications(): void {
    this.sofNotificationsFloatingModal.events$
      .pipe(takeUntil(this.destroy$))
      .subscribe((evt) => {
        switch (evt.type) {
          case 'markAllRequested':
            this.sofNotificationsFloatingModal.markAllRead();
            break;
          case 'clearAllRequested':
            this.sofNotificationsFloatingModal.clearAll();
            break;
          case 'markOneRequested':
            this.sofNotificationsFloatingModal.markOneRead(evt.id);
            break;
          case 'deleteOneRequested':
            this.sofNotificationsFloatingModal.deleteOne(evt.id);
            break;
        }
      });

    this.sofNotificationsFloatingModal.items$
      .pipe(takeUntil(this.destroy$))
      .subscribe((list) => {
        const unread = list.filter((n) => !n.read).length;
        if (unread > 0) this.sofHeaderService.setBadge('notifications', unread);
        else this.sofHeaderService.clearBadge('notifications');
      });
  }

  private initProfile(): void {
    const user = this.auth.user();
    this.sofUserMenuService.setProfile({
      fullName: user?.sName ?? 'Admin',
      email: user?.email ?? '',
      roleLabel: user?.rol ?? '',
    });
    this.sofUserMenuService.setItems([
      { id: 'logout', label: 'Cerrar sesión', icon: 'fa fa-sign-out' },
    ]);
    this.sofUserMenuService.events$.subscribe((item) => {
      if (item?.type === 'itemRequested' && item.item?.id === 'logout') {
        this.handleLogout();
      }
    });
  }

  handleSelectMenu(item: ISofMenuItem): void {
    if (item.action === 'route') {
      this.sofMenuService.select(item.id);
      void this.router.navigateByUrl(item.href ?? '');
    }
  }

  private syncMenuSelection(rawUrl: string): void {
    const url = rawUrl.split('?')[0].split('#')[0];

    if (url === '/') {
      this.sofMenuService.drillReset();
      this.sofMenuService.select('welcome');
      return;
    }

    // Buscar en items raíz (workspace + item sin hijos)
    for (const item of this.menu) {
      if ((item.type === 'item' || item.type === 'workspace') && !item.children?.length && item.href && item.href !== '/') {
        if (url.startsWith(item.href)) {
          this.sofMenuService.drillReset();
          this.sofMenuService.select(item.id);
          return;
        }
      }
    }

    // Buscar en children de los grupos
    for (const group of this.menu) {
      if (!group.children?.length) continue;
      const child = group.children.find(
        c => c.href && c.href !== '/' && url.startsWith(c.href)
      );
      if (child) {
        this.sofMenuService.drillReset();
        this.sofMenuService.drillDown(group);
        this.sofMenuService.select(child.id);
        return;
      }
    }
  }

  handleSelectMenuHeader(item: ISofHeaderEvent): void {
    if (item.item.id === 'notifications' && item.originalEvent) {
      this.openFloatingNotifications(item.originalEvent);
    }
    if (item.item.id === 'theme') {
      this._isDark = !this._isDark;
      document.documentElement.dataset['colorScheme'] = this._isDark ? 'dark' : 'light';
      localStorage.setItem('sof-color-scheme', this._isDark ? 'dark' : 'light');
      const themeBtn = this.menuHeader.find((m) => m.id === 'theme');
      if (themeBtn) {
        themeBtn.icon    = this._isDark ? 'fa fa-sun'  : 'fa fa-moon';
        themeBtn.tooltip = this._isDark ? 'Modo claro' : 'Modo oscuro';
        this.sofHeaderService.setMenu([...this.menuHeader]);
      }
    }
  }

  private openFloatingNotifications(e: Event): void {
    this.sofFloatingModalService.openFloatingModal({
      id: 'modal-notifications',
      component: SofNotificationsFloatingModalComponent,
      data: { config: this.configNotifications },
      position: 'near',
      triangle: true,
      width: 360,
      withBackdrop: false,
      event: e as MouseEvent,
    });
  }

  private openFloatingUser(e: Event): void {
    this.sofFloatingModalService.openFloatingModal({
      id: this.idFloatingUser,
      component: SofUserFloatingModalComponent,
      data: { version: '1.0.0' },
      position: 'near',
      triangle: true,
      width: 300,
      withBackdrop: false,
      event: e as MouseEvent,
    });
  }

  async handleLogout(): Promise<void> {
    this.sofToastService.openToast({
      id: 'alert-close-session',
      title: '¡Éxito!',
      message: 'Sesión cerrada correctamente.',
      position: 'top-right',
    });
    this.sofFloatingModalService.closeModalById(this.idFloatingUser);
    await this.auth.logoutAsync();
    void this.router.navigate(['/login']);
  }

  private _orgSwitcherItem(id: string, orgName: string): ISofMenuItem {
    return {
      id,
      label: orgName,
      tooltip: 'Cambiar organización',
      type: 'workspace',
      action: 'route',
      href: '/organizaciones',
    };
  }

  private _buildMenu(user = this.auth.user()): ISofMenuItem[] {
    const orgName = user?.orgName || user?.tenantSlug || 'Mi organización';
    const hasOrg  = !!user?.orgId;
    const isOrgAdmin = hasOrg && (user?.rol === 'owner' || user?.rol === 'admin');
    return [
      // ── Org indicator (root) ─────────────────────────────────────────
      this._orgSwitcherItem('org-switcher', orgName),
      // ── Inicio ───────────────────────────────────────────────────────
      {
        id: 'welcome',
        label: 'Inicio',
        tooltip: 'Inicio',
        iconStart: 'fa fa-house',
        type: 'item',
        action: 'route',
        href: '/',
        show: hasOrg,
      },
      // ── Ecommerce (grupo con hijos) ───────────────────────────────────
      {
        id: 'grp-ecommerce',
        label: 'Ecommerce',
        tooltip: 'Ecommerce',
        iconStart: 'fa fa-store',
        type: 'item',
        show: hasOrg,
        children: [
          this._orgSwitcherItem('org-sw-ecm', orgName),
          { id: 'productos',    label: 'Productos',       tooltip: 'Productos',       iconStart: 'fa fa-box',     type: 'item', action: 'route', href: '/productos' },
          { id: 'categorias',   label: 'Categorías',      tooltip: 'Categorías',      iconStart: 'fa fa-tags',    type: 'item', action: 'route', href: '/categorias' },
          { id: 'pedidos',      label: 'Pedidos',         tooltip: 'Pedidos',         iconStart: 'fa fa-receipt', type: 'item', action: 'route', href: '/pedidos' },
          { id: 'clientes',     label: 'Clientes',        tooltip: 'Clientes',        iconStart: 'fa fa-users',   type: 'item', action: 'route', href: '/clientes' },
          { id: 'balances',     label: 'Balances',        tooltip: 'Balances',        iconStart: 'fa fa-wallet',  type: 'item', action: 'route', href: '/balances' },
          { id: 'costos-envio', label: 'Costos de envío', tooltip: 'Costos de envío', iconStart: 'fa fa-truck',   type: 'item', action: 'route', href: '/costos-envio' },
          { id: 'descuentos',   label: 'Descuentos',      tooltip: 'Descuentos',      iconStart: 'fa fa-tag',     type: 'item', action: 'route', href: '/descuentos' },
        ],
      },
      // ── QR Dinámicos (grupo con hijos) ───────────────────────────────
      {
        id: 'grp-qr',
        label: 'QR Dinámicos',
        tooltip: 'QR Dinámicos',
        iconStart: 'fa fa-qrcode',
        type: 'item',
        show: hasOrg,
        children: [
          this._orgSwitcherItem('org-sw-qr', orgName),
          { id: 'qr-dinamicos', label: 'QR Dinámicos', tooltip: 'QR Dinámicos', iconStart: 'fa fa-qrcode', type: 'item', action: 'route', href: '/qr-dinamicos' },
        ],
      },
      // ── WEBLY-WEB (grupo con hijos) ──────────────────────────────────
      {
        id: 'grp-webpages',
        label: 'WEBLY-WEB',
        tooltip: 'Sitio web',
        iconStart: 'fa fa-globe',
        type: 'item',
        show: hasOrg,
        children: [
          this._orgSwitcherItem('org-sw-web', orgName),
          { id: 'plantillas-web', label: 'Plantillas',   tooltip: 'Plantillas de sitio web', iconStart: 'fa fa-file-code', type: 'item', action: 'route', href: '/plantillas-web' },
          { id: 'paginas-web',    label: 'Páginas',      tooltip: 'Páginas publicadas',       iconStart: 'fa fa-file',      type: 'item', action: 'route', href: '/paginas-web' },
          { id: 'formularios',    label: 'Formularios',  tooltip: 'Formularios recibidos',    iconStart: 'fa fa-envelope',  type: 'item', action: 'route', href: '/formularios' },
        ],
      },
      // ── Cuenta (grupo con hijos) — siempre visible ───────────────────
      {
        id: 'grp-cuenta',
        label: 'Cuenta',
        tooltip: 'Cuenta',
        iconStart: 'fa fa-user-circle',
        type: 'item',
        children: [
          this._orgSwitcherItem('org-sw-cnt', orgName),
          { id: 'organizaciones',  label: 'Organizaciones',  tooltip: 'Mis organizaciones',  iconStart: 'fa fa-sitemap',     type: 'item', action: 'route', href: '/organizaciones' },
          { id: 'equipo',          label: 'Equipo',          tooltip: 'Gestión del equipo',   iconStart: 'fa fa-users',       type: 'item', action: 'route', href: '/equipo',      show: hasOrg },
          { id: 'mi-organizacion',  label: 'Mi organización', tooltip: 'Nombre, slug y dominios', iconStart: 'fa fa-building', type: 'item', action: 'route', href: '/mi-organizacion', show: isOrgAdmin },
          { id: 'suscripcion',     label: 'Suscripción',     tooltip: 'Plan y uso',           iconStart: 'fa fa-credit-card', type: 'item', action: 'route', href: '/suscripcion',     show: hasOrg },
          { id: 'datos-fiscales',  label: 'Datos fiscales',  tooltip: 'Perfiles de facturación', iconStart: 'fa fa-file-invoice', type: 'item', action: 'route', href: '/datos-fiscales', show: isOrgAdmin },
          { id: 'actividad',      label: 'Actividad',       tooltip: 'Acciones recientes',   iconStart: 'fa fa-clock-rotate-left', type: 'item', action: 'route', href: '/actividad', show: isOrgAdmin },
          { id: 'mi-cuenta',       label: 'Mi cuenta',       tooltip: 'Mi cuenta',            iconStart: 'fa fa-user-circle', type: 'item', action: 'route', href: '/mi-cuenta',        show: hasOrg },
        ],
      },
    ];
  }
}
